import { t } from '@lingui/core/macro';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiDummyButton } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useStationaryBookingActions } from '../../../../stores/StationaryBookingStore';
import type { BookingChoicePhoto } from './bookingChoicePhotos';

interface Props {
  isBookable: boolean;
  photo?: BookingChoicePhoto;
}

/** Primary path: dark hero card that links straight to the home-delivery address step. */
export function HomeDeliveryCard({ isBookable, photo }: Props) {
  const { clearStore } = useStationaryBookingActions();
  const benefits = [t`Tire change at home or at work`, t`Flexible times - also weekends`];

  return (
    <NoddiBasicCard
      to={isBookable ? customerRoutes.newBookingAddress.getPath() : undefined}
      onClick={clearStore}
      disabled={!isBookable}
      disableActiveScale
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-[22px] border-none bg-[#131313] p-0 shadow-none md:rounded-3xl md:hover:shadow-none',
        !isBookable && 'cursor-not-allowed opacity-60'
      )}
    >
      {photo && (
        <div className='relative h-[190px] shrink-0 md:h-60'>
          <picture>
            <source srcSet={photo.avif} type='image/avif' />
            <source srcSet={photo.webp} type='image/webp' />
            <img
              src={photo.jpg}
              alt={
                // eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term
                t`Technician changing tires at the customer's home`
              }
              className='absolute inset-0 size-full object-cover'
            />
          </picture>
          {/* Scrim that fuses the photo into the dark card surface */}
          <div className='pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(19,19,19,0)_45%,rgba(19,19,19,0.92)_100%)]' />
        </div>
      )}
      <div className='flex flex-1 flex-col gap-5 p-5 pb-6 md:gap-6 md:p-6'>
        <h2 className='text-2xl leading-tight font-bold tracking-tight text-white md:text-3xl'>
          {t`Home delivery for tire change and tire hotel`}
        </h2>
        <div className='flex flex-col gap-3 md:grid md:grid-cols-2 md:gap-x-6 md:gap-y-4'>
          {benefits.map((benefit) => (
            <div key={benefit} className='flex items-start gap-3'>
              <NoddiIcon name='Check' size='small' strokeWidth={2.6} className='mt-0.5 shrink-0 text-accent-on-dark' />
              <span className='text-sm leading-snug text-neutral-200'>{benefit}</span>
            </div>
          ))}
        </div>
        <NoddiDummyButton
          endIcon={isBookable ? 'ArrowRight' : undefined}
          className='mt-auto h-14 w-full rounded-xl text-base font-semibold'
        >
          {isBookable ? t`Check if we deliver to you` : t`No available appointments in the near future`}
        </NoddiDummyButton>
      </div>
    </NoddiBasicCard>
  );
}
